import { useCallback, useEffect, useRef, useState } from 'react';

// Lightning's `web/src/hooks/usePolling.ts` uses a callback-based shape
// (`onSuccess` callback, no return value). The video pages were ported
// over from a different repo that uses a `{ data, loading, error,
// refresh }` return shape, which composes more naturally with React
// state. Both signatures coexist; this one is the "data-returning"
// variant scoped to the video subtree to avoid forcing the lightning
// pages onto a new API.

export interface PollingResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
  deps: ReadonlyArray<unknown> = [],
): PollingResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const tick = useCallback(async () => {
    try {
      const result = await fetcherRef.current();
      if (cancelledRef.current) return;
      setData(result);
      setError(null);
    } catch (ex) {
      if (cancelledRef.current) return;
      setError(ex instanceof Error ? ex.message : 'Unknown error');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    tick();
    const id = window.setInterval(tick, intervalMs);
    return () => {
      cancelledRef.current = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, tick, ...deps]);

  return { data, loading, error, refresh: tick };
}
